import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { ROUTES_CONFIG } from "src/constants/routesConfig";

interface ModalProps {
  vendorName: string;
}

interface SuccessProps extends ModalProps {
  onClose?: () => void;
}

interface WarningProps extends ModalProps {
  onCancel: () => void;
  onConfirm: () => void;
}

interface ErrorProps extends ModalProps {
  onRetry: () => void;
  onClose?: () => void;
}

const Success: React.FC<SuccessProps> = ({ vendorName, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <CheckCircle2 className="w-6 h-6 text-success-600" />
            <h2 className="text-lg font-semibold text-gray-900">Vendor Updated</h2>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
              &times;
            </button>
          )}
        </div>

        <div className="p-5 space-y-4">
          <div className="bg-success-50 border border-success-200 rounded-lg p-4">
            <p className="text-sm text-gray-700">The vendor has been successfully updated</p>
            <p className="text-sm text-gray-700 mt-1">
              <span className="font-semibold text-success-700">Vendor name: {vendorName}</span>
            </p>
          </div>

          {/* Home Button */}
          <div className="flex justify-center pt-2">
            <Link to={ROUTES_CONFIG.home.path}>
              <button className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium">
                Home
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const DeleteSuccess: React.FC<SuccessProps> = ({ vendorName, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <CheckCircle2 className="w-6 h-6 text-success-600" />
            <h2 className="text-lg font-semibold text-gray-900">Vendor Deleted</h2>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
              &times;
            </button>
          )}
        </div>

        <div className="p-5 space-y-4">
          <div className="bg-success-50 border border-success-200 rounded-lg p-4">
            <p className="text-sm text-gray-700">The vendor has been successfully deleted</p>
            <p className="text-sm text-gray-700 mt-1">
              <span className="font-semibold text-success-700">Vendor name: {vendorName}</span>
            </p>
          </div>

          {/* Home Button */}
          <div className="flex justify-center pt-2">
            <Link to={ROUTES_CONFIG.home.path}>
              <button className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium">
                Home
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const DeleteWarning: React.FC<WarningProps> = ({ vendorName, onConfirm, onCancel }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="w-6 h-6 text-error-600" />
            <h2 className="text-lg font-semibold text-gray-900">Delete Vendor</h2>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
            &times;
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="bg-error-50 border border-error-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-error-600 mt-0.5 mr-2 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-error-800 mb-1">
                  Are you sure you want to delete this vendor?
                </p>
                <p className="text-sm text-error-700">This action cannot be undone</p>
                <p className="text-sm text-error-700 mt-1">
                  <span className="font-semibold">Vendor name: {vendorName}</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="flex justify-end space-x-3 p-5 border-t border-gray-200 bg-gray-50">
          <button
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm font-medium"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 bg-error-600 text-white rounded-md hover:bg-error-700 transition-colors text-sm font-medium"
            onClick={onConfirm}
          >
            Delete Vendor
          </button>
        </div>
      </div>
    </div>
  );
};

const Error: React.FC<ErrorProps> = ({ onRetry, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <XCircle className="w-6 h-6 text-error-600" />
            <h2 className="text-lg font-semibold text-gray-900">Update Failed</h2>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
              &times;
            </button>
          )}
        </div>

        <div className="p-5">
          <p className="text-sm text-gray-700 text-center mb-6">
            An error occurred while updating the vendor. Please try again.
          </p>

          {/* Footer Button */}
          <div className="flex justify-center">
            <button
              className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium"
              onClick={onRetry}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const UpdateVendorModals = {
  Success,
  DeleteSuccess,
  DeleteWarning,
  Error
};

export { UpdateVendorModals };
